/**
 * Invoice encoding / decoding
 *
 * All invoice data lives in the URL hash — no server needed.
 * The hash is a Base64url-encoded JSON blob, same pattern as receipts.
 */

export interface InvoiceLineItem {
  description: string;
  qty: number;
  price: number; // unit fiat price
}

export interface InvoiceData {
  /** Schema version */
  v: 1;
  /** Unique invoice ID */
  id: string;
  /** Merchant / freelancer name */
  merchantName: string;
  merchantTagline?: string;
  logoDataUrl?: string;
  brandColor?: string;
  /** Client name */
  clientName: string;
  /** Client email (for display only) */
  clientEmail?: string;
  /** Currency code */
  currency: string;
  /** Line items */
  items: InvoiceLineItem[];
  /** Subtotal */
  subtotal: number;
  /** Tax rate % */
  taxRate: number;
  taxName: string;
  taxAmount: number;
  /** Grand total */
  total: number;
  /** Optional note / memo */
  note?: string;
  /** BCH receiving address */
  address: string;
  /** Due date (unix ms) — 0 means no due date */
  dueDate: number;
  /** Created timestamp (unix ms) */
  createdAt: number;
}

/** Stored invoice record (local only) */
export interface StoredInvoice {
  id: string;
  clientName: string;
  total: number;
  currency: string;
  createdAt: number;
  dueDate: number;
  url: string;
  paid: boolean;
  paidAt?: number;
}

const INVOICES_KEY = 'bch-pos:invoices';

export function loadInvoices(): StoredInvoice[] {
  try {
    const raw = localStorage.getItem(INVOICES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as StoredInvoice[];
  } catch { return []; }
}

export function saveInvoices(invoices: StoredInvoice[]): void {
  try {
    localStorage.setItem(INVOICES_KEY, JSON.stringify(invoices.slice(0, 200)));
  } catch (e) { console.warn('Failed to save invoices', e); }
}

export function addInvoice(invoice: StoredInvoice): void {
  const list = loadInvoices();
  list.unshift(invoice);
  saveInvoices(list);
}

export function markInvoicePaid(id: string): void {
  const list = loadInvoices();
  const idx = list.findIndex((i) => i.id === id);
  if (idx >= 0) {
    list[idx] = { ...list[idx], paid: true, paidAt: Date.now() };
    saveInvoices(list);
  }
}

export function deleteInvoice(id: string): void {
  saveInvoices(loadInvoices().filter((i) => i.id !== id));
}

// ─── URL encoding ────────────────────────────────────────────────────────────

function toBase64Url(str: string): string {
  return btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(str: string): string {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return decodeURIComponent(escape(atob(padded + pad)));
}

/**
 * Compact encoding — single-letter keys, no logo, omit zero/empty fields.
 * This shrinks URLs from ~2000+ chars to ~300–500.
 *
 * Key map:
 *   v=v, i=id, m=merchantName, mt=merchantTagline, bc=brandColor,
 *   c=clientName, ce=clientEmail, cu=currency, it=items (d/q/p per item),
 *   s=subtotal, tr=taxRate, tn=taxName, ta=taxAmount, t=total,
 *   n=note, a=address, dd=dueDate, ca=createdAt
 */

interface CompactItem { d: string; q: number; p: number }

interface CompactInvoice {
  v: 1;
  i: string;
  m: string;
  mt?: string;
  bc?: string;
  c: string;
  ce?: string;
  cu: string;
  it: CompactItem[];
  s: number;
  tr?: number;
  tn?: string;
  ta?: number;
  t: number;
  n?: string;
  a: string;
  dd?: number;
  ca: number;
}

function toCompact(data: InvoiceData): CompactInvoice {
  const c: CompactInvoice = {
    v: 1,
    i: data.id.slice(0, 8), // short ID is enough for display
    m: data.merchantName,
    c: data.clientName,
    cu: data.currency,
    it: data.items.map((item) => ({ d: item.description, q: item.qty, p: item.price })),
    s: data.subtotal,
    t: data.total,
    a: data.address,
    ca: data.createdAt,
  };
  // Only include non-empty / non-zero optional fields
  if (data.merchantTagline) c.mt = data.merchantTagline;
  if (data.brandColor && data.brandColor !== '#0AC18E') c.bc = data.brandColor;
  if (data.clientEmail) c.ce = data.clientEmail;
  if (data.taxRate > 0) { c.tr = data.taxRate; c.tn = data.taxName; c.ta = data.taxAmount; }
  if (data.note) c.n = data.note;
  if (data.dueDate > 0) c.dd = data.dueDate;
  return c;
}

function fromCompact(c: CompactInvoice): InvoiceData {
  return {
    v: 1,
    id: c.i,
    merchantName: c.m,
    merchantTagline: c.mt,
    brandColor: c.bc,
    clientName: c.c,
    clientEmail: c.ce,
    currency: c.cu,
    items: c.it.map((item) => ({ description: item.d, qty: item.q, price: item.p })),
    subtotal: c.s,
    taxRate: c.tr ?? 0,
    taxName: c.tn ?? 'Tax',
    taxAmount: c.ta ?? 0,
    total: c.t,
    note: c.n,
    address: c.a,
    dueDate: c.dd ?? 0,
    createdAt: c.ca,
  };
}

export function encodeInvoice(data: InvoiceData): string {
  return toBase64Url(JSON.stringify(toCompact(data)));
}

export function decodeInvoice(hash: string): InvoiceData | null {
  try {
    const clean = hash.startsWith('#') ? hash.slice(1) : hash;
    const parsed = JSON.parse(fromBase64Url(clean));
    if (parsed?.v !== 1) return null;
    // Check if it's compact format (has 'i' key) or legacy (has 'id' key)
    if ('i' in parsed && 'c' in parsed && 'cu' in parsed) {
      return fromCompact(parsed as CompactInvoice);
    }
    // Legacy full-key format
    return parsed as InvoiceData;
  } catch {
    return null;
  }
}

export function buildInvoiceUrl(data: InvoiceData): string {
  const encoded = encodeInvoice(data);
  const base = window.location.origin;
  return `${base}/invoice#${encoded}`;
}
